import { Briefcase, Calendar, Mail, MapPin, Phone } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Datepicker, Label, TextInput } from "flowbite-react";
import { useSearchParams } from "react-router-dom";


import Modal from "../../lib/ui/components/Modal";
import Button from "../../lib/ui/components/button/Button";
import Tooltip from "../../lib/ui/components/tooltip/Tooltip";
import {
  Heading3,
  Heading5,
  NormalParagraph,
  SmallText,
  XsmallText,
} from "../../lib/ui/components/typography/Typography";

const PROFILE_STORAGE_KEY = "june_candidate_profile_proto";
const CANDIDATE_AVATAR_URL = "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=256&h=256&q=80";

/** Prototype-only mock profile (not persisted from backend). */
const DEFAULT_PROFILE = {
  name: "Michael Anderson",
  roleSkillset: "Senior IT Specialist",
  location: "Evanston, IL",
  experience: "12+ years",
  availability: "Immediately",
  email: "michael.anderson@example.com",
  phone: "(847) 555-0123",
};

function valueOrFallback(value, fallback) {
  const normalized = String(value ?? "").trim();
  return normalized || fallback;
}

/** Format a phone number string to (XXX) XXX-XXXX. */
function formatPhoneNumber(value) {
  const digits = String(value ?? "").replace(/\D/g, "").slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** Parse availability date string to Date object, or return null. */
function parseAvailabilityDate(value) {
  if (!value || value === "Immediately") return null;
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
}

/** Format a Date to a readable string like "Apr 15, 2026". */
function formatAvailabilityDate(date) {
  if (!date) return "Immediately";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const datepickerTheme = {
  root: {
    input: {
      field: {
        icon: {
          base: "pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3",
          svg: "h-5 w-5 text-gray-400",
        },
        input: {
          withIcon: {
            on: "pl-3 pr-10",
            off: "pl-3",
          },
        },
      },
    },
  },
  popup: {
    root: {
      base: "absolute top-10 z-[9999] block pt-2",
      inner: "inline-block rounded-lg bg-white p-4 shadow-lg border border-gray-200",
    },
  },
};

function MetadataItem({ icon: Icon, iconTooltip, children }) {
  const iconNode = <Icon className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden />;
  return (
    <span className="inline-flex items-center gap-1.5 text-gray-600">
      {iconTooltip ? <Tooltip content={iconTooltip}>{iconNode}</Tooltip> : iconNode}
      <SmallText className="text-gray-600">{children}</SmallText>
    </span>
  );
}

const INTERVIEW_COVERAGE = [
  { id: 1, role: "Senior Frontend Engineer", company: "Acme Corp", coveragePercent: 92 },
  { id: 2, role: "Platform Engineer", company: "Globex Inc", coveragePercent: 68 },
  { id: 3, role: "Security Analyst", company: "Initech", coveragePercent: 41 },
];

const TOP_MATCHING_RESPONSES = [
  {
    id: 1,
    role: "Senior Frontend Engineer",
    company: "Acme Corp",
    responses: [
      {
        summary: "Explains how to scale a design system with component contracts and visual regression checks.",
        transcript: {
          question: "Can you describe how you scale a design system without slowing product delivery?",
          answer:
            "I treat the design system like a product with clear contracts. Every shared component has usage guidance, accessibility requirements, and a small API surface so teams can adopt it without guessing. Before introducing new variants, I check if composition solves the need first. On rollout, I pair Storybook documentation with visual regression tests in CI, so teams can move quickly while we catch style drift early. That lets product teams ship fast without creating one-off components we have to unwind later.",
        },
      },
      {
        summary: "Walks through performance debugging from browser traces to concrete render optimizations.",
        transcript: {
          question: "When an app feels slow, what does your performance-debugging process look like?",
          answer:
            "I start with real browser traces to find where users are actually paying the cost, then narrow it down to the largest blocking renders or expensive effects. In one recent case, we had repeated re-renders in a dashboard due to unstable props and over-broad selectors. I added memoization where it mattered, split heavy sections with lazy loading, and moved expensive transforms out of render paths. We tracked the impact with before-and-after profiles and reduced interaction latency from roughly 450ms to under 180ms.",
        },
      },
    ],
  },
  {
    id: 2,
    role: "Platform Engineer",
    company: "Globex Inc",
    responses: [
      {
        summary: "Describes ownership boundaries between product teams and platform services.",
        transcript: {
          question: "How do you define ownership between platform and product teams?",
          answer:
            "I define platform ownership around paved-road capabilities, while product teams own business outcomes and service behavior. Platform is responsible for standards, templates, and shared tooling that remove repetitive work. Product teams stay accountable for their SLAs and domain logic. To avoid ambiguity, we publish ownership boundaries in runbooks and service catalogs, then review pain points in monthly operating reviews. That balance keeps autonomy high without fragmenting reliability practices.",
        },
      },
      {
        summary: "Outlines practical incident response steps, including communication and post-incident learning.",
        transcript: {
          question: "Tell me how you run incident response in practice.",
          answer:
            "I optimize for fast triage and clear communication. First we assign an incident lead, a comms owner, and someone focused on mitigation. We open a shared timeline immediately so decisions are documented in real time. During mitigation, updates go out on a fixed cadence with impact, actions, and next checkpoint. After recovery, we run a blameless review focused on system improvements, then track follow-up actions with owners and due dates so learning turns into measurable reliability gains.",
        },
      },
    ],
  },
];

function loadPersistedState() {
  if (typeof window === "undefined") {
    return { profile: DEFAULT_PROFILE, resume: null };
  }

  try {
    const raw = localStorage.getItem(PROFILE_STORAGE_KEY);
    if (!raw) return { profile: DEFAULT_PROFILE, resume: null };

    const parsed = JSON.parse(raw);
    const merged = { ...DEFAULT_PROFILE, ...(parsed.profile || {}) };
    return {
      profile: {
        ...merged,
        email: valueOrFallback(merged.email, DEFAULT_PROFILE.email),
        phone: valueOrFallback(merged.phone, DEFAULT_PROFILE.phone),
        experience: valueOrFallback(merged.experience, DEFAULT_PROFILE.experience),
        availability: valueOrFallback(merged.availability, DEFAULT_PROFILE.availability),
      },
      resume: parsed.resume || null,
    };
  } catch (error) {
    return { profile: DEFAULT_PROFILE, resume: null };
  }
}

function persistState(profile, resume) {
  if (typeof window === "undefined") return;
  localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify({ profile, resume }));
}

function formatDateTime(isoTimestamp) {
  if (!isoTimestamp) return "";
  const date = new Date(isoTimestamp);
  return date.toLocaleString();
}

function CandidateProfile() {
  const initialState = useMemo(() => loadPersistedState(), []);
  const [profile, setProfile] = useState(initialState.profile);
  const [draftProfile, setDraftProfile] = useState(initialState.profile);
  const [resume, setResume] = useState(initialState.resume);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [activeTranscript, setActiveTranscript] = useState(null);
  const resumeInputRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("edit") !== "1") return;
    setDraftProfile(profile);
    setIsEditModalOpen(true);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("edit");
        return next;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams, profile]);

  const openEditModal = () => {
    setDraftProfile(profile);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setDraftProfile(profile);
  };

  const saveProfile = () => {
    setProfile(draftProfile);
    persistState(draftProfile, resume);
    setIsEditModalOpen(false);
  };

  const handleResumeUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const nextResume = {
      fileName: file.name,
      updatedAt: new Date().toISOString(),
    };

    setResume(nextResume);
    persistState(profile, nextResume);

    event.target.value = "";
  };

  const removeResume = () => {
    setResume(null);
    persistState(profile, null);
  };

  const openTranscriptModal = (item, response) => {
    setActiveTranscript({
      role: item.role,
      company: item.company,
      question: response.transcript.question,
      answer: response.transcript.answer,
    });
  };

  const closeTranscriptModal = () => {
    setActiveTranscript(null);
  };

  const displayExperience = valueOrFallback(profile.experience, DEFAULT_PROFILE.experience);
  const displayAvailability = valueOrFallback(profile.availability, DEFAULT_PROFILE.availability);
  const displayEmail = valueOrFallback(profile.email, DEFAULT_PROFILE.email);
  const displayPhone = valueOrFallback(profile.phone, DEFAULT_PROFILE.phone);

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      <header className="relative mb-8 overflow-hidden rounded-2xl border border-slate-200/70 bg-[linear-gradient(118deg,#f0f9ff_0%,#f5f3ff_38%,#e0e7ff_70%,rgba(253,242,248,0.42)_100%)] p-6 shadow-sm">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_10%_20%,rgba(255,255,255,0.65),transparent_42%),radial-gradient(circle_at_90%_80%,rgba(255,255,255,0.45),transparent_38%)]" />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
          <div className="flex gap-4 sm:gap-5 min-w-0 flex-1">
            <img
              src={CANDIDATE_AVATAR_URL}
              alt={`${profile.name} profile`}
              className="h-20 w-20 shrink-0 rounded-full object-cover ring-2 ring-white/70 shadow-sm sm:h-24 sm:w-24"
            />
            <div className="min-w-0 flex-1 pt-0.5">
              <Heading3 className="mb-1 !text-[color:var(--primary-900)]">{profile.name}</Heading3>
              <NormalParagraph className="text-gray-600 mb-3">{profile.roleSkillset}</NormalParagraph>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <MetadataItem icon={MapPin} iconTooltip="Location">
                  {profile.location}
                </MetadataItem>
                <MetadataItem icon={Briefcase} iconTooltip="Years of experience">
                  <span className="text-gray-700">{displayExperience}</span>
                </MetadataItem>
                <MetadataItem icon={Calendar} iconTooltip="Availability">
                  <span className="text-gray-700">{displayAvailability}</span>
                </MetadataItem>
              </div>
            </div>
          </div>
          <Button variant="primary" onClick={openEditModal} className="shrink-0 self-start sm:mt-1">
            Edit Profile
          </Button>
        </div>

        <div className="relative my-5 border-t border-white/50" role="presentation" />

        <div className="relative flex flex-wrap items-center gap-x-8 gap-y-2">
          <MetadataItem icon={Mail} iconTooltip="Email">
            <span className="text-gray-700 font-semibold">{displayEmail}</span>
          </MetadataItem>
          <MetadataItem icon={Phone} iconTooltip="Phone">
            <span className="text-gray-700 font-semibold">{displayPhone}</span>
          </MetadataItem>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-8">
          <section>
            <div className="flex items-center justify-between mb-4">
              <Heading5>Interview Coverage</Heading5>
            </div>
            <div className="space-y-4">
              {INTERVIEW_COVERAGE.map((item) => (
                <div key={item.id} className="rounded-lg border border-gray-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="min-w-0">
                      <SmallText weight="bold" className="text-gray-900 mb-1">{item.role}</SmallText>
                      <SmallText className="text-gray-500">{item.company}</SmallText>
                    </div>
                    <SmallText weight="semibold" className="text-gray-700">{item.coveragePercent}%</SmallText>
                  </div>
                  <div
                    className="h-2 w-full rounded-full overflow-hidden"
                    style={{ backgroundColor: "var(--primary-100)" }}
                  >
                    <div
                      className="h-full rounded-full transition-[width] duration-300 ease-out"
                      style={{
                        width: `${item.coveragePercent}%`,
                        backgroundColor: "var(--primary-main)",
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <div className="mb-4">
              <Heading5>Top Matching Responses</Heading5>
            </div>
            <div className="space-y-4">
              {TOP_MATCHING_RESPONSES.map((item) => (
                <div key={item.id} className="rounded-lg border border-gray-200 bg-white p-4">
                  <SmallText weight="bold" className="text-gray-900 mb-1">{item.role}</SmallText>
                  <XsmallText className="text-gray-500 mb-3 block">{item.company}</XsmallText>
                  <div className="space-y-2">
                    {item.responses.map((response, responseIndex) => (
                      <button
                        key={`${item.id}-${responseIndex}`}
                        type="button"
                        onClick={() => openTranscriptModal(item, response)}
                        className="group block w-full text-left bg-transparent p-0 border-0"
                        aria-label={`Open transcript for matching response ${responseIndex + 1} for ${item.role}`}
                      >
                        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 transition-all duration-150 group-hover:border-[color:var(--primary-main)] group-hover:bg-white group-hover:shadow-sm group-focus-visible:outline group-focus-visible:outline-2 group-focus-visible:outline-offset-2 group-focus-visible:outline-[color:var(--primary-main)]">
                          <XsmallText className="text-gray-700">{response.summary}</XsmallText>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="lg:col-span-1">
          <div className="rounded-xl bg-gray-50 p-5 h-fit lg:sticky lg:top-24">
            <Heading5 className="mb-3">Resume</Heading5>
            {resume ? (
              <>
                <SmallText weight="semibold" className="text-gray-800 block mb-1">{resume.fileName}</SmallText>
                <XsmallText className="text-gray-500 block mb-4">
                  Last updated: {formatDateTime(resume.updatedAt)}
                </XsmallText>
              </>
            ) : (
              <SmallText className="text-gray-500 block mb-4">No resume uploaded yet.</SmallText>
            )}
            <input
              ref={resumeInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx"
              onChange={handleResumeUpload}
            />
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              <Button variant="secondary" onClick={() => resumeInputRef.current?.click()}>
                Upload Resume
              </Button>
              {resume && (
                <Button variant="tertiary" onClick={removeResume}>
                  Remove
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal show={isEditModalOpen} onClose={closeEditModal} size="3xl">
        <Modal.Header>Edit Profile</Modal.Header>
        <Modal.Body className="overflow-visible">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
            <div>
              <div className="mb-2 block">
                <Label htmlFor="candidateName" value="Full Name" />
              </div>
              <TextInput
                id="candidateName"
                type="text"
                value={draftProfile.name}
                onChange={(event) =>
                  setDraftProfile((prev) => ({ ...prev, name: event.target.value }))
                }
                placeholder="Enter full name"
              />
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="candidateRoleSkillset" value="Role / Skillset" />
              </div>
              <TextInput
                id="candidateRoleSkillset"
                type="text"
                value={draftProfile.roleSkillset}
                onChange={(event) =>
                  setDraftProfile((prev) => ({ ...prev, roleSkillset: event.target.value }))
                }
                placeholder="e.g. Senior Frontend Engineer"
              />
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="candidateLocation" value="Location" />
              </div>
              <TextInput
                id="candidateLocation"
                type="text"
                value={draftProfile.location}
                onChange={(event) =>
                  setDraftProfile((prev) => ({ ...prev, location: event.target.value }))
                }
                placeholder="e.g. Evanston, IL"
              />
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="candidateExperience" value="Experience" />
              </div>
              <TextInput
                id="candidateExperience"
                type="text"
                value={draftProfile.experience ?? ""}
                onChange={(event) =>
                  setDraftProfile((prev) => ({ ...prev, experience: event.target.value }))
                }
                placeholder="e.g. 12+ years"
              />
            </div>

            <div className="relative overflow-visible">
              <div className="mb-2 block">
                <Label htmlFor="candidateAvailability" value="Available From" />
              </div>
              <Datepicker
                id="candidateAvailability"
                value={parseAvailabilityDate(draftProfile.availability)}
                onChange={(date) =>
                  setDraftProfile((prev) => ({
                    ...prev,
                    availability: date ? formatAvailabilityDate(date) : "Immediately",
                  }))
                }
                minDate={new Date()}
                label="Select availability date"
                theme={datepickerTheme}
                showClearButton={false}
                showTodayButton={false}
              />
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="candidateEmail" value="Email" />
              </div>
              <TextInput
                id="candidateEmail"
                type="email"
                value={draftProfile.email ?? ""}
                onChange={(event) =>
                  setDraftProfile((prev) => ({ ...prev, email: event.target.value }))
                }
                placeholder="name@email.com"
              />
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="candidatePhone" value="Phone" />
              </div>
              <TextInput
                id="candidatePhone"
                type="tel"
                value={draftProfile.phone ?? ""}
                onChange={(event) => {
                  const formatted = formatPhoneNumber(event.target.value);
                  setDraftProfile((prev) => ({ ...prev, phone: formatted }));
                }}
                placeholder="(000) 000-0000"
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex justify-end gap-3 w-full">
            <Button variant="secondary" onClick={closeEditModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={saveProfile}>
              Save Changes
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(activeTranscript)} onClose={closeTranscriptModal} size="2xl" dismissible>
        <Modal.Header className="!border-b border-gray-200">
          <div>
            <span className="block text-h5 font-semibold text-gray-900">Interview transcript</span>
            {activeTranscript ? (
              <span className="mt-1 block text-small text-gray-500">
                {activeTranscript.role} · {activeTranscript.company}
              </span>
            ) : null}
          </div>
        </Modal.Header>
        <Modal.Body className="max-h-[min(70vh,560px)] overflow-y-auto pt-2 sm:pt-4">
          {activeTranscript ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-gray-200 bg-white p-4 sm:p-5 shadow-sm">
                <p className="text-small font-bold text-gray-900 leading-relaxed">
                  June: {activeTranscript.question}
                </p>
                <p className="mt-3 text-small font-normal text-gray-600 leading-relaxed">
                  Applicant: {activeTranscript.answer}
                </p>
              </div>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer className="!border-t border-gray-200">
          <div className="flex justify-end w-full">
            <Button variant="secondary" onClick={closeTranscriptModal}>
              Close
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default CandidateProfile;
